import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Alert, Box, Button, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { useFamilyChild } from './FamilyShell';
import { parentApi } from './parentApi';
import { formatWhen } from './familyFormat';
import { openFamilyReport } from './openFamilyReport';

export default function FamilyHome() {
  const { child } = useFamilyChild();
  const navigate = useNavigate();
  const q = useQuery(
    ['familyToday', child.studentId],
    async () => (await parentApi.today(child.studentId)).data?.data,
    { enabled: !!child.studentId }
  );
  const data = q.data || {};
  const next = data.nextClass;
  const last = data.lastClass;
  const needs = data.needsYou || [];

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>{child.firstName}</Typography>

      {needs.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 3 }}>
          <Typography fontWeight={700} sx={{ mb: 1 }}>Needs you</Typography>
          {needs.map((item) => (
            <Box key={`${item.kind}-${item.sessionId || item.threadId || item.reportId}`} sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
              {item.meta ? <Typography variant="caption" color="text.secondary">{item.meta}</Typography> : null}
              <Box sx={{ mt: 0.5 }}>
                {item.kind === 'homework' && item.sessionId && (
                  <Button size="small" variant="contained" onClick={() => navigate(`/family/homework/${item.sessionId}`)}>
                    Open homework
                  </Button>
                )}
                {item.kind === 'reply' && item.threadId && (
                  <Button size="small" onClick={() => navigate(`/family/messages/${item.threadId}`)}>
                    View reply
                  </Button>
                )}
                {item.kind === 'report' && item.reportId && (
                  <Button size="small" onClick={() => openFamilyReport(child.studentId, item.reportId)}>
                    View report
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Alert>
      )}

      {q.isLoading ? (
        <Card sx={{ mb: 2 }}><CardContent><Skeleton height={48} /></CardContent></Card>
      ) : next ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Next class</Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>{formatWhen(next)}</Typography>
            <Typography color="text.secondary">with {next.teacherName}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              {next.joinUrl ? (
                <Button variant="contained" href={next.joinUrl} target="_blank" rel="noreferrer">
                  Join class
                </Button>
              ) : null}
              <Button
                variant={next.joinUrl ? 'outlined' : 'contained'}
                onClick={() => navigate(`/family/leave?sessionId=${encodeURIComponent(next.sessionId)}`)}
              >
                Can't attend
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {last && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Last class</Typography>
          <Card sx={{ cursor: 'pointer' }} onClick={() => navigate(`/family/classes/${last.sessionId}`)}>
            <CardContent>
              <Typography fontWeight={700}>
                {formatWhen(last)}{last.attendanceLabel ? ` · ${last.attendanceLabel}` : ''}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {last.recap ? String(last.recap).slice(0, 120) : last.homeworkSent ? 'Homework sent' : 'Open recap'}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
}
